/**
 * Cross-Project Stats Tab
 * Coverage analysis combining extension clicks with crawler places
 */

import { Row, Col, Card, Statistic, Table, Empty, Alert, Skeleton } from 'antd';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useCrossProjectStats } from '@/hooks/useProjectStats';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { CHART_COLORS } from '@/styles/theme';
import { formatNumber } from '@/utils/formatters';

export function CrossProjectTab() {
  const { data, isLoading, isError, error } = useCrossProjectStats();
  const isDark = usePreferencesStore((s) => s.theme) === 'dark';

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[1, 2, 3].map((i) => <Card key={i} size="small"><Skeleton active paragraph={{ rows: 4 }} /></Card>)}
    </div>
  );
  if (isError) return <Alert type="error" message="Failed to load cross-project stats" description={(error as Error)?.message} showIcon />;
  if (!data) return <Empty description="Extension database not connected" />;

  const gridColor = isDark ? '#303030' : '#f0f0f0';
  const textColor = isDark ? '#8c8c8c' : '#595959';
  const bgColor = isDark ? '#1f1f1f' : '#fff';
  const tooltipStyle = { backgroundColor: bgColor, border: `1px solid ${gridColor}`, borderRadius: 8 };

  const { kpis } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        {[
          { title: 'Total Places', value: kpis.totalPlaces, color: CHART_COLORS.primary },
          { title: 'Total Clicks', value: kpis.totalClicks, color: CHART_COLORS.success },
          { title: 'Reviewed Places', value: kpis.reviewedPlaces, color: CHART_COLORS.purple },
          { title: 'Coverage Rate', value: kpis.coverageRate, suffix: '%', color: CHART_COLORS.cyan },
          { title: 'Backlog', value: kpis.backlog, color: CHART_COLORS.warning },
          { title: 'Active Users (7d)', value: kpis.activeUsers7d, color: CHART_COLORS.magenta },
          { title: 'Avg Daily Clicks (30d)', value: kpis.avgDailyClicks30d, color: CHART_COLORS.orange },
          { title: 'Completed Jobs', value: kpis.completedJobs, color: CHART_COLORS.success },
        ].map((kpi) => (
          <Col key={kpi.title} xs={12} sm={8} lg={6} xl={3}>
            <Card size="small" styles={{ body: { padding: 16 } }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>{kpi.title}</span>}
                value={kpi.value}
                suffix={kpi.suffix}
                formatter={(v) => formatNumber(v as number)}
                valueStyle={{ fontSize: 22, color: kpi.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Coverage Over Time */}
      <Card title="Coverage Over Time (Cumulative)" size="small">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: textColor, fontSize: 11 }}
              type="category"
              allowDuplicatedCategory={false}
            />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatNumber(v)} />
            <Legend />
            <Line data={data.coverageOverTime.places} type="monotone" dataKey="cumulative" name="Scraped" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
            <Line data={data.coverageOverTime.reviewed} type="monotone" dataKey="cumulative" name="Reviewed" stroke={CHART_COLORS.success} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Row gutter={16}>
        {/* Per-User Reviews */}
        <Col xs={24} lg={12}>
          <Card title="Reviews per User" size="small">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.perUserReviews} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                <XAxis type="number" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <YAxis type="category" dataKey="name" tick={{ fill: textColor, fontSize: 11 }} width={55} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Reviewed']} />
                <Bar dataKey="count" fill={CHART_COLORS.purple} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Click Depth */}
        <Col xs={24} lg={12}>
          <Card title="Click Depth per Place (distinct users)" size="small">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.clickDepth} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="depth" tick={{ fill: textColor, fontSize: 11 }} label={{ value: 'Users', position: 'insideBottom', offset: -5, fill: textColor }} />
                <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Places']} />
                <Bar dataKey="count" fill={CHART_COLORS.cyan} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Daily Throughput */}
      <Card title="Daily Pipeline Throughput (30 days)" size="small">
        {(() => {
          // Merge scraped and reviewed into one series by date
          const dateMap = new Map<string, { date: string; scraped: number; reviewed: number }>();
          for (const d of data.dailyThroughput.scraped) {
            dateMap.set(d.date, { date: d.date, scraped: d.count, reviewed: 0 });
          }
          for (const d of data.dailyThroughput.reviewed) {
            const existing = dateMap.get(d.date);
            if (existing) existing.reviewed = d.count;
            else dateMap.set(d.date, { date: d.date, scraped: 0, reviewed: d.count });
          }
          const merged = [...dateMap.values()].sort((a, b) => a.date.localeCompare(b.date));

          return (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={merged} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatNumber(v)} />
                <Legend />
                <Line type="monotone" dataKey="scraped" name="Scraped" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="reviewed" name="Reviewed" stroke={CHART_COLORS.success} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          );
        })()}
      </Card>

      {/* Review Rates by Search Query */}
      <Card title="Review Rate by Search Query" size="small">
        <Table
          dataSource={data.jobReviewRates}
          rowKey="name"
          size="small"
          pagination={{ pageSize: 15 }}
          columns={[
            { title: 'Search Query', dataIndex: 'name', key: 'name', ellipsis: true },
            { title: 'Jobs', dataIndex: 'jobCount', key: 'jobCount', width: 70, align: 'right' as const },
            { title: 'Total Places', dataIndex: 'totalPlaces', key: 'totalPlaces', width: 110, align: 'right' as const, render: (v: number) => formatNumber(v) },
            { title: 'Reviewed', dataIndex: 'reviewed', key: 'reviewed', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
            {
              title: 'Coverage', dataIndex: 'reviewPct', key: 'reviewPct', width: 100, align: 'right' as const,
              sorter: (a, b) => a.reviewPct - b.reviewPct,
              render: (v: number) => <span style={{ color: v >= 50 ? CHART_COLORS.success : v >= 20 ? CHART_COLORS.warning : CHART_COLORS.error }}>{v}%</span>,
            },
          ]}
        />
      </Card>

      {/* Category Review Bias - Grouped Bar Chart */}
      <Card title="Category Review Bias" size="small">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data.categoryBias.slice(0, 15)} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis
              dataKey="category"
              tick={{ fill: textColor, fontSize: 10 }}
              angle={-35}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatNumber(v)} />
            <Legend />
            <Bar dataKey="total" name="Total" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
            <Bar dataKey="reviewed" name="Reviewed" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Category Review Bias - Table */}
      <Card title="Category Review Bias (Detail)" size="small">
        <Table
          dataSource={data.categoryBias.slice(0, 30)}
          rowKey="category"
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
          columns={[
            { title: 'Category', dataIndex: 'category', key: 'category', ellipsis: true },
            { title: 'Total', dataIndex: 'total', key: 'total', width: 90, align: 'right' as const, render: (v: number) => formatNumber(v) },
            { title: 'Reviewed', dataIndex: 'reviewed', key: 'reviewed', width: 90, align: 'right' as const, render: (v: number) => formatNumber(v) },
            {
              title: 'Review %', dataIndex: 'reviewPct', key: 'reviewPct', width: 100, align: 'right' as const,
              sorter: (a, b) => a.reviewPct - b.reviewPct,
              render: (v: number) => <span style={{ color: v >= 50 ? CHART_COLORS.success : v >= 20 ? CHART_COLORS.warning : CHART_COLORS.error }}>{v}%</span>,
            },
          ]}
        />
      </Card>
    </div>
  );
}
